import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';

import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeadCell,
  TableHeadRow,
  TableRow,
  useRouteDocumentTitle,
} from '@client/toolkit';

import {
  BuildingFilterPanel,
  KpiTile,
  PLACEHOLDER,
  ReportTableSkeletonRows,
  formatNumber,
  formatPercent,
  useBuildingFilter,
} from '../components/portfolio-report';
import { ReportingPaths } from '../reporting.router';
import { ReportPageContent } from '../../../components/layout/report-page-content';
import { PercentageBar, YearPicker } from '../../../components';
import { useYearParam } from '../../../hooks';
import { useGetReportingPortfolioOverviewQuery } from '@client/api';

// Currency figures on this report are shown in kNOK (thousands).
function formatKNok(value: number | null | undefined): string {
  if (value == null || Number.isNaN(value)) return PLACEHOLDER;
  return `${formatNumber(value / 1000, 0)} kNOK`;
}

// Valuation shown in millions ("Verdsettelse (MNOK)").
function formatMillions(value: number | null | undefined): string {
  if (value == null || Number.isNaN(value)) return PLACEHOLDER;
  return formatNumber(value / 1_000_000, 1);
}

// Faktisk-vs-Budsjett bar shown under the two top KPI boxes. The bar scales to the larger of the
// two figures so the fill (actual) and the tick (budget) read against the same axis.
function BudgetProgress({
  actual,
  budget,
  actualLabel,
  budgetLabel,
}: {
  actual: number;
  budget: number;
  actualLabel: string;
  budgetLabel: string;
}) {
  // Scale by the larger magnitude so the bar works for negative figures too (e.g. a budgeted
  // loss on Driftsresultat). The marker is kept within a small inset so it stays visible at the
  // edges instead of being clipped to the rounded corner.
  const scale = Math.max(Math.abs(actual), Math.abs(budget));
  if (scale <= 0) return null;
  const clamp = (n: number, min = 0, max = 100) => Math.min(max, Math.max(min, n));
  const fill = clamp((actual / scale) * 100);
  const marker = clamp((budget / scale) * 100, 2, 98);
  return (
    <div className="mt-2 flex flex-col gap-1.5">
      <PercentageBar className="h-2" value={`${fill}`} markerValue={`${marker}`} />
      <div className="flex items-center gap-3 text-xs text-white/60">
        <span className="flex items-center gap-1.5">
          <span className="inline-block w-2.5 h-2.5 rounded-sm bg-[#3F98B4]" />
          {actualLabel}
        </span>
        <span className="flex items-center gap-1.5">
          <span className="inline-block w-0.5 h-3 bg-white" />
          {budgetLabel} {formatKNok(budget)}
        </span>
      </div>
    </div>
  );
}

export function ReportingFinancialOverviewRoute() {
  const { t } = useTranslation('reporting');
  useRouteDocumentTitle(t('financialOverview.documentTitle'), ReportingPaths.FinancialOverview);

  const { selectedYear, setSelectedYear } = useYearParam();

  // This report aggregates the financial report selected for the chosen year per building. We use
  // isFetching (not isLoading) so skeletons also show while refetching after a year change.
  const { data, isFetching } = useGetReportingPortfolioOverviewQuery({
    startDate: selectedYear.startOf('year').format('YYYY-MM-DD'),
    endDate: selectedYear.endOf('year').format('YYYY-MM-DD'),
  });

  const buildings = useMemo(() => data?.buildings ?? [], [data]);
  const { buildingFilter, setBuildingFilter, activeTags, toggleTag, allTags, filtered } = useBuildingFilter(buildings);

  // KPIs follow the active filters so the boxes always describe the set shown in the table.
  // Egenkapitalprosent is aggregated as Σ equity / Σ total capital rather than averaging ratios.
  const kpis = useMemo(() => {
    let rentalIncome = 0;
    let commonCostIncome = 0;
    let ebitda = 0;
    let equity = 0;
    let totalCapital = 0;
    let interestBearingDebt = 0;
    let budgetRentalIncome = 0;
    let budgetCommonCostIncome = 0;
    let budgetEbitda = 0;
    let valuation = 0;
    let valuationArea = 0;
    let hasBudget = false;

    filtered.forEach((b) => {
      rentalIncome += b.financialRentalIncome ?? 0;
      commonCostIncome += b.financialCommonCostIncome ?? 0;
      ebitda += b.financialEbitda ?? 0;
      equity += b.financialEquity ?? 0;
      totalCapital += b.financialTotalCapital ?? 0;
      interestBearingDebt += b.interestBearingDebt ?? 0;
      budgetRentalIncome += b.budgetRentalIncome ?? 0;
      budgetCommonCostIncome += b.budgetCommonCostIncome ?? 0;
      budgetEbitda += b.budgetEbitda ?? 0;
      // Σ yield valuation, with the matching area so the box can show a portfolio NOK/m².
      if (b.yieldValuation != null) {
        valuation += b.yieldValuation;
        valuationArea += b.grossAreaM2 ?? 0;
      }
      if (
        b.budgetRentalIncome != null ||
        b.budgetCommonCostIncome != null ||
        b.budgetEbitda != null
      ) {
        hasBudget = true;
      }
    });

    return {
      rentalIncome,
      commonCostIncome,
      ebitda,
      equityRatio: totalCapital > 0 ? (equity / totalCapital) * 100 : null,
      interestBearingDebt,
      budgetRentalIncome,
      budgetCommonCostIncome,
      budgetEbitda,
      // Null (not 0) when no building has a CSV-backed valuation, so the card shows the "–"
      // placeholder and clears alongside the columns when financial reports are removed.
      valuation: valuationArea > 0 ? valuation : null,
      valuationPerM2: valuationArea > 0 ? valuation / valuationArea : null,
      hasBudget,
    };
  }, [filtered]);

  const filterPanel = (
    <BuildingFilterPanel
      title={t('financialOverview.filter.title')}
      searchPlaceholder={t('financialOverview.filter.building')}
      noTagsLabel={t('financialOverview.filter.noTags')}
      buildingFilter={buildingFilter}
      onBuildingFilterChange={setBuildingFilter}
      allTags={allTags}
      activeTags={activeTags}
      onToggleTag={toggleTag}
      action={<YearPicker value={selectedYear} onChange={setSelectedYear} />}
    />
  );

  return (
    <ReportPageContent breadcrumb={t('financialOverview.breadcrumb')} menu={filterPanel}>
      <div className="flex gap-5 justify-between min-h-[146px] text-white">
        <KpiTile
          label={t('financialOverview.kpi.rentalIncome')}
          sub={t('financialOverview.kpi.rentalIncomeSub')}
          value={formatKNok(kpis.rentalIncome)}
          loading={isFetching}
          footer={
            kpis.hasBudget ? (
              <BudgetProgress
                actual={kpis.rentalIncome}
                budget={kpis.budgetRentalIncome}
                actualLabel={t('financialOverview.kpi.actual')}
                budgetLabel={t('financialOverview.kpi.budgetLabel')}
              />
            ) : undefined
          }
        />
        <KpiTile
          label={t('financialOverview.kpi.commonCostIncome')}
          sub={t('financialOverview.kpi.commonCostIncomeSub')}
          value={formatKNok(kpis.commonCostIncome)}
          loading={isFetching}
          footer={
            kpis.hasBudget ? (
              <BudgetProgress
                actual={kpis.commonCostIncome}
                budget={kpis.budgetCommonCostIncome}
                actualLabel={t('financialOverview.kpi.actual')}
                budgetLabel={t('financialOverview.kpi.budgetLabel')}
              />
            ) : undefined
          }
        />
        <KpiTile
          label={t('financialOverview.kpi.ebitda')}
          sub={t('financialOverview.kpi.ebitdaSub')}
          value={formatKNok(kpis.ebitda)}
          loading={isFetching}
          footer={
            kpis.hasBudget ? (
              <BudgetProgress
                actual={kpis.ebitda}
                budget={kpis.budgetEbitda}
                actualLabel={t('financialOverview.kpi.actual')}
                budgetLabel={t('financialOverview.kpi.budgetLabel')}
              />
            ) : undefined
          }
        />
        <KpiTile
          label={t('financialOverview.kpi.equityRatio')}
          sub={t('financialOverview.kpi.equityRatioSub')}
          value={formatPercent(kpis.equityRatio)}
          tooltip={t('financialOverview.kpi.equityRatioTooltip')}
          loading={isFetching}
        />
        <KpiTile
          label={t('financialOverview.kpi.interestBearingDebt')}
          sub={t('financialOverview.kpi.interestBearingDebtSub')}
          value={formatKNok(kpis.interestBearingDebt)}
          tooltip={t('financialOverview.kpi.interestBearingDebtTooltip')}
          loading={isFetching}
        />
        <KpiTile
          label={t('financialOverview.kpi.valuation')}
          sub={t('financialOverview.kpi.valuationSub')}
          value={formatMillions(kpis.valuation)}
          tooltip={t('financialOverview.kpi.valuationTooltip')}
          loading={isFetching}
        />
      </div>

      <Card>
        <CardHeader>
          <CardTitle variant="highlighted">{t('financialOverview.register.title')}</CardTitle>
        </CardHeader>
        <CardContent variant="fill" className="px-4 pb-4">
          <Table className="w-full" size="small">
            <TableHead>
              <TableHeadRow>
                <TableHeadCell>{t('financialOverview.register.header.building')}</TableHeadCell>
                <TableHeadCell align="right">{t('financialOverview.register.header.year')}</TableHeadCell>
                <TableHeadCell align="right">{t('financialOverview.register.header.grossRentalIncome')}</TableHeadCell>
                <TableHeadCell align="right">{t('financialOverview.register.header.equityRatio')}</TableHeadCell>
                <TableHeadCell align="right">{t('financialOverview.register.header.interestBearingDebt')}</TableHeadCell>
                <TableHeadCell align="right">{t('financialOverview.register.header.area')}</TableHeadCell>
                <TableHeadCell align="right">{t('financialOverview.register.header.valuationM')}</TableHeadCell>
                <TableHeadCell align="right">{t('financialOverview.register.header.valuationPerM2')}</TableHeadCell>
              </TableHeadRow>
            </TableHead>
            <TableBody>
              {isFetching ? (
                <ReportTableSkeletonRows columns={8} />
              ) : filtered.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={8}>
                    <span className="text-white/60">{t('financialOverview.register.empty')}</span>
                  </TableCell>
                </TableRow>
              ) : (
                filtered.map((b) => (
                    <TableRow key={b.id}>
                      <TableCell>
                        <Link
                          to={`/portfolio/buildings/${b.id}`}
                          className="font-medium text-white hover:underline focus:outline-none focus:underline"
                        >
                          {b.name}
                        </Link>
                      </TableCell>
                      <TableCell align="right">{b.financialYear ?? PLACEHOLDER}</TableCell>
                      <TableCell align="right">{formatKNok(b.financialGrossRentalIncome)}</TableCell>
                      <TableCell align="right">{formatPercent(b.equityRatioPercent)}</TableCell>
                      <TableCell align="right">{formatKNok(b.interestBearingDebt)}</TableCell>
                      <TableCell align="right">{formatNumber(b.grossAreaM2)}</TableCell>
                      <TableCell align="right">{formatMillions(b.yieldValuation)}</TableCell>
                      <TableCell align="right">{formatNumber(b.yieldValuationPerM2)}</TableCell>
                    </TableRow>
                  ))
              )}
            </TableBody>
          </Table>
        </CardContent>
      </Card>
    </ReportPageContent>
  );
}
